// Delivery tracking for bulk SMS notifications: status labels, grouping of
// recipient statuses, dispatch progress and polling intervals.
import moment from 'moment';

export const NOTIFICATION_STATUS = {
	DRAFT: 'DRAFT',
	SCHEDULED: 'SCHEDULED',
	PROCESSING: 'PROCESSING',
	SENT: 'SENT',
	FAILED: 'FAILED',
};

export const AUDIENCE_TYPES = {
	VENUES: 'VENUES',
	ALL: 'ALL',
};

export const ALL_MEMBERS_LABEL = 'All active members';

const NOTIFICATION_STATUS_LABELS = {
	[NOTIFICATION_STATUS.PROCESSING]: 'Sending…',
	[NOTIFICATION_STATUS.SENT]: 'Sent',
	[NOTIFICATION_STATUS.FAILED]: 'Failed',
};

export function getNotificationStatusLabel(status) {
	return NOTIFICATION_STATUS_LABELS[status] || status || '';
}

// Recipient statuses, keyed the same way as the `stats` object returned by
// the API (lowercase). PENDING and PROCESSING are uppercase in the API.
export const RECIPIENT_STATUSES = [
	'pending',
	'processing',
	'accepted',
	'queued',
	'sending',
	'sent',
	'delivered',
	'undelivered',
	'failed',
	'canceled',
];

export const RECIPIENT_STATUS_GROUPS = {
	DELIVERED: 'delivered',
	SENT: 'sent',
	IN_PROGRESS: 'inProgress',
	FAILED: 'failed',
	CANCELED: 'canceled',
};

// Display order of the groups in the stats card.
export const RECIPIENT_STATUS_GROUP_LIST = [
	{
		key: RECIPIENT_STATUS_GROUPS.DELIVERED,
		label: 'Delivered',
		statuses: ['delivered'],
	},
	{
		key: RECIPIENT_STATUS_GROUPS.SENT,
		label: 'Sent (unconfirmed)',
		statuses: ['sent'],
	},
	{
		key: RECIPIENT_STATUS_GROUPS.IN_PROGRESS,
		label: 'In progress',
		statuses: ['pending', 'processing', 'accepted', 'queued', 'sending'],
	},
	{
		key: RECIPIENT_STATUS_GROUPS.FAILED,
		label: 'Failed',
		statuses: ['undelivered', 'failed'],
	},
	{
		key: RECIPIENT_STATUS_GROUPS.CANCELED,
		label: 'Canceled',
		statuses: ['canceled'],
	},
];

const RECIPIENT_STATUS_LABELS = {
	pending: 'Pending',
	processing: 'Pending',
	accepted: 'Queued',
	queued: 'Queued',
	sending: 'Queued',
	sent: 'Sent (unconfirmed)',
	delivered: 'Delivered',
	undelivered: 'Failed',
	failed: 'Failed',
	canceled: 'Canceled',
};

// Labels for the status filter, where each option is one exact API status
// (the recipients endpoint filters by a single status), so statuses sharing a
// table label need a label of their own.
const RECIPIENT_STATUS_FILTER_LABELS = {
	pending: 'Pending',
	processing: 'Processing',
	accepted: 'Accepted',
	queued: 'Queued',
	sending: 'Sending',
	sent: 'Sent (unconfirmed)',
	delivered: 'Delivered',
	undelivered: 'Undelivered',
	failed: 'Failed',
	canceled: 'Canceled',
};

const normalizeStatus = (status) => String(status || '').toLowerCase();

export function getRecipientStatusLabel(status) {
	return RECIPIENT_STATUS_LABELS[normalizeStatus(status)] || status || '—';
}

export function getRecipientStatusFilterLabel(status) {
	return RECIPIENT_STATUS_FILTER_LABELS[normalizeStatus(status)] || status;
}

export function getRecipientStatusGroup(status) {
	const normalized = normalizeStatus(status);
	const group = RECIPIENT_STATUS_GROUP_LIST.find(({ statuses }) =>
		statuses.includes(normalized),
	);
	return group ? group.key : null;
}

// Every count defaults to 0 so the UI never has to guard against a missing
// `stats` (old notifications, or the compact stats of the list endpoint).
export function normalizeRecipientStats(stats) {
	const normalized = { total: Number(stats?.total) || 0 };
	RECIPIENT_STATUSES.forEach((status) => {
		normalized[status] = Number(stats?.[status]) || 0;
	});
	return normalized;
}

export function getInTransitCount(stats) {
	const s = normalizeRecipientStats(stats);
	return s.pending + s.processing + s.accepted + s.queued + s.sending;
}

// Share of recipients already handed to Twilio, from 0 to 100.
export function getDispatchProgress(stats) {
	const s = normalizeRecipientStats(stats);
	if (s.total === 0) return 0;
	const dispatched = s.total - s.pending - s.processing;
	return Math.min(100, Math.max(0, (dispatched / s.total) * 100));
}

export function getPercentage(count, total) {
	if (!total) return 0;
	return (count / total) * 100;
}

export function formatPercentage(count, total) {
	const percentage = getPercentage(count, total);
	if (percentage === 0 || percentage === 100) return `${percentage}%`;
	return `${percentage.toFixed(1)}%`;
}

// Counts and percentages per status group, in display order.
export function groupRecipientStats(stats) {
	const s = normalizeRecipientStats(stats);
	return RECIPIENT_STATUS_GROUP_LIST.map(({ key, label, statuses }) => {
		const count = statuses.reduce((sum, status) => sum + s[status], 0);
		return {
			key,
			label,
			statuses,
			count,
			percentage: getPercentage(count, s.total),
		};
	});
}

export const PROCESSING_POLLING_INTERVAL = 5000;
export const IN_TRANSIT_POLLING_INTERVAL = 15000;
export const LIST_POLLING_INTERVAL = 30000;

// Polling interval for a notification detail (and its recipients):
// fast while messages are being handed to Twilio, slower while Twilio is
// still reporting statuses, and none once nothing is in transit.
export function getNotificationPollingInterval(notification) {
	if (!notification || notification.error) return false;
	if (notification.status === NOTIFICATION_STATUS.PROCESSING) {
		return PROCESSING_POLLING_INTERVAL;
	}
	if (getInTransitCount(notification.stats) > 0) {
		return IN_TRANSIT_POLLING_INTERVAL;
	}
	return false;
}

export function getNotificationsListPollingInterval(notifications) {
	if (!Array.isArray(notifications)) return false;
	const isSending = notifications.some(
		(notification) => notification?.status === NOTIFICATION_STATUS.PROCESSING,
	);
	return isSending ? LIST_POLLING_INTERVAL : false;
}

// ISO date to local date and time, or null when there is no date.
export function formatDateTime(isoDate) {
	if (!isoDate) return null;
	const date = moment(isoDate);
	return date.isValid() ? date.format('MM/DD/YYYY h:mm A') : null;
}
